import React from 'react'

import { BorderStyle, Box, Color, Display, FlexDirection } from '@gousto-internal/citrus-react'
import type {
  DashboardExperienceOrderNewAndLimitedResponse,
  DashboardExperienceOrderTopPicksResponse,
} from '@library/api-dashboard-experience'

import {
  RecommendationSection,
  type RecommendationSectionData,
} from './RecommendationSection/RecommendationSection'

type OrderRecommendationsProps = {
  topPicks: DashboardExperienceOrderTopPicksResponse | null
  newAndLimited: DashboardExperienceOrderNewAndLimitedResponse | null
  orderId: string
}

const getSections = ({
  topPicks,
  newAndLimited,
}: Pick<OrderRecommendationsProps, 'topPicks' | 'newAndLimited'>): RecommendationSectionData[] =>
  [
    topPicks
      ? {
          collection: 'top picks',
          id: 'top-picks',
          title: topPicks.title,
          recipes: topPicks.recipes,
        }
      : null,
    newAndLimited
      ? {
          collection: 'limited edition',
          id: 'new-and-limited',
          title: newAndLimited.title,
          recipes: newAndLimited.recipes,
        }
      : null,
  ].filter((section): section is RecommendationSectionData =>
    Boolean(section && section.recipes.length > 0),
  )

export const OrderRecommendations = ({
  topPicks,
  newAndLimited,
  orderId,
}: OrderRecommendationsProps) => {
  const sections = getSections({
    topPicks,
    newAndLimited,
  })

  if (sections.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="order-recommendations"
      width="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      borderWidth={0.5}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_100}
      paddingBottom={4}
      paddingH={4}
      paddingV={4}
      paddingRight={4}
      bg={Color.White}
      style={{
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {sections.map((section) => (
        <RecommendationSection key={section.id} orderId={orderId} section={section} />
      ))}
    </Box>
  )
}
